import React from 'react';
import { motion } from 'framer-motion';
import '../assets/styles/PackagesPage.css';
import starterImg1 from '../assets/images/essentials-img-1.png';
import starterImg2 from '../assets/images/essentials-img-2.png';
import authorityImg1 from '../assets/images/enterprise-img-1.png';
import authorityImg2 from '../assets/images/enterprise-img-2.png';

const packages = [
  {
    id: 'starter',
    number: 'Package 01',
    name: 'Starter',
    price: '$997',
    timeline: '2 weeks',
    intro: 'For businesses that need a clean, professional website quickly without custom functionality.',
    included: [
      ['4-page website', 'Home, About, Services, and Contact.'],
      ['Template-based build', 'Customized colors, content, layout, and brand feel.'],
      ['Mobile responsive', 'Designed to work cleanly on phones, tablets, and desktop.'],
      ['Lead capture basics', 'Contact form, Google Analytics, and basic on-page SEO.'],
    ],
    bestFor: [
      'Local businesses that need a credible online presence fast',
      'New service providers validating their offer',
      'Simple brochure sites with no custom logic',
    ],
    process: ['Starter intake', 'Assets and content collection', 'Website build', 'Revision and launch'],
    benefits: ['Fast launch', 'Lower investment', 'Clear scope', '30 days post-launch support'],
    images: [starterImg1, starterImg2],
    ctaClass: 'btn-primary-warm',
  },
  {
    id: 'growth',
    number: 'Package 02',
    name: 'Growth',
    price: '$1,997',
    timeline: '3-4 weeks',
    intro: 'The primary offer for small businesses that want a custom website built to create trust and generate better leads.',
    included: [
      ['Custom website up to 7 pages', 'Built around your services, proof, calls to action, and local market.'],
      ['Messaging guidance', 'Clear positioning, page structure, and service explanations.'],
      ['Local SEO foundation', 'Search-friendly page setup, metadata, and service-area structure.'],
      ['Launch setup', 'Domain, hosting, Google Analytics, and Search Console.'],
    ],
    bestFor: [
      'Local service businesses that want more inquiries',
      'Businesses replacing an outdated DIY or template site',
      'Owners who need help turning their offer into clear website copy',
    ],
    process: ['Kickoff call', 'Growth intake', 'Messaging and sitemap', 'Custom build', 'Two revision rounds', 'Launch'],
    benefits: ['Custom design', 'Lead-focused structure', '60 days post-launch support', 'Best balance of price and outcome'],
    images: [authorityImg1, starterImg2],
    ctaClass: 'btn-accent',
  },
  {
    id: 'authority',
    number: 'Package 03',
    name: 'Authority',
    price: '$3,500',
    timeline: '3-5 weeks',
    intro: 'For businesses that need more than a brochure site: custom logic, booking flows, calculators, portals, lead funnels, or workflow tools.',
    included: [
      ['Custom website up to 12 pages', 'A larger site with room for deeper services, proof, content, and conversion paths.'],
      ['Process audit', 'We map the workflow before build so the custom functionality is scoped correctly.'],
      ['Scoped custom functionality', 'Booking, calculator, portal, funnel, CMS, basic store, or custom form logic.'],
      ['Testing and performance pass', 'A more careful launch process for custom features and workflows.'],
    ],
    bestFor: [
      'Businesses with manual processes that should move online',
      'Service businesses that need intake, booking, portals, or quote logic',
      'Owners who want the website to become part of operations, not just marketing',
    ],
    process: ['Process audit', 'Scope lock', 'Workflow map', 'Design direction', 'Build', 'Testing', 'Launch'],
    benefits: ['Custom business logic', 'Stronger differentiation', '90 days post-launch support', 'Care Plan Plus recommended'],
    images: [authorityImg1, authorityImg2],
    ctaClass: 'btn-primary-warm',
  },
];

const renderPackage = (pkg, index) => (
  <motion.section
    key={pkg.id}
    id={pkg.id}
    className={`package-section ${pkg.id}-package`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: 'easeOut' }}
    viewport={{ once: true, amount: 0.1 }}
  >
    <div className="intro-section">
      <p className="eyebrow centered">{pkg.number}</p>
      <h2 className="centered">{pkg.name}</h2>
      <p className="package-price">Starting at <strong>{pkg.price}</strong> &nbsp;|&nbsp; Timeline: <strong>{pkg.timeline}</strong></p>
      <p className="intro-text">{pkg.intro}</p>
    </div>

    <div className="two-column-layout">
      {index % 2 === 1 && (
        <div className="image-column">
          {pkg.images.map((image, i) => <img key={image} src={image} alt={`${pkg.name} package example ${i + 1}`} />)}
        </div>
      )}

      <div className="text-column">
        <h3 className="section-title">What's included</h3>
        <ul className="features-list">
          {pkg.included.map(([title, detail]) => (
            <li key={title}><strong>{title}:</strong> {detail}</li>
          ))}
        </ul>

        <h3 className="section-title">Best for</h3>
        <ul className="target-list">
          {pkg.bestFor.map(item => <li key={item}>{item}</li>)}
        </ul>

        <h3 className="section-title">Process overview</h3>
        <ol className="process-list">
          {pkg.process.map(item => <li key={item}><strong>{item}</strong></li>)}
        </ol>

        <h3 className="section-title">Why {pkg.name}?</h3>
        <ul className="benefits-list">
          {pkg.benefits.map(item => <li key={item}>{item}</li>)}
        </ul>
      </div>

      {index % 2 === 0 && (
        <div className="image-column">
          {pkg.images.map((image, i) => <img key={image} src={image} alt={`${pkg.name} package example ${i + 1}`} />)}
        </div>
      )}
    </div>

    <div className="btn-wrapper">
      <a href="/get-started" className={pkg.ctaClass}>Get started</a>
    </div>
  </motion.section>
);

const PackagesPage = () => (
  <div className="packages-page">
    <section className="packages-hero">
      <div className="packages-hero-inner">
        <motion.p className="eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          Pricing
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
          Websites built around<br />
          <span>where your business is going.</span>
        </motion.h1>
        <motion.p className="packages-hero-sub" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
          Choose a fast brochure site, a custom lead-focused website, or a site with custom business logic.
        </motion.p>
        <motion.div className="cta-group" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
          <a href="#starter" className="btn-primary-warm">Starter</a>
          <a href="#growth" className="btn-accent">Growth</a>
          <a href="#authority" className="btn-primary-warm">Authority</a>
        </motion.div>
      </div>
    </section>

    {packages.map(renderPackage)}

    <motion.section className="common-features" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true, amount: 0.2 }}>
      <p className="eyebrow centered" style={{ color: 'rgba(242,241,237,0.5)' }}>Care Plan</p>
      <h3>Keep the site handled after launch</h3>
      <ul className="common-features-list">
        <li>Monthly backups and uptime monitoring</li>
        <li>Up to 1 hour of small edits per month</li>
        <li>Priority email support</li>
        <li>Basic plan from $149/mo and Plus plan from $199/mo</li>
      </ul>
      <p className="cta">
        <strong style={{ color: 'rgba(242,241,237,0.9)' }}>Not sure which package fits?</strong><br />
        Start with Growth unless you only need a simple 4-page site or clearly need custom logic.
      </p>
      <a href="/get-started" className="btn-accent">Get started</a>
    </motion.section>
  </div>
);

export default PackagesPage;
